/**
 * TanStack Query hooks for email templates + signature + cold bulk-send.
 *
 * Server state for the Settings "Modelli email" manager (org + personal
 * templates), the per-user signature override (effective = user ?? org), the
 * template picker fused into the email / cold-email composer, and the
 * prospecting bulk cold-send. Mirrors ``useSalesQueries.ts`` style: a typed
 * ``KEY`` map + thin query/mutation wrappers that invalidate the touched
 * caches on success.
 */
import {
  bulkSend,
  createEmailTemplate,
  createSignature,
  deleteEmailTemplate,
  deleteSignature,
  getEffectiveSignature,
  getEmailSignature,
  listEmailTemplates,
  listSignatureAccounts,
  listSignatures,
  setAccountSignature,
  setDefaultSignature,
  setEmailSignature,
  updateEmailTemplate,
  updateSignature,
  type AccountSignatureBinding,
  type BulkSendInput,
  type BulkSendResponse,
  type CreateEmailTemplateInput,
  type CreateSignatureInput,
  type EffectiveSignature,
  type EmailSignature,
  type EmailTemplate,
  type EmailTemplateKind,
  type Signature,
  type UpdateEmailTemplateInput,
  type UpdateSignatureInput,
} from "@ando/core/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const KEY = {
  templates: (kind: EmailTemplateKind) =>
    ["email", "templates", kind] as const,
  templatesAll: ["email", "templates"] as const,
  signature: ["email", "signature"] as const,
  effectiveSignature: ["email", "signature", "effective"] as const,
  // Pool (multi-signature) keys.
  signatures: ["email", "signatures"] as const,
  signatureAccounts: ["email", "signatures", "accounts"] as const,
};

export function useEmailTemplates(kind: EmailTemplateKind = "both") {
  return useQuery<EmailTemplate[]>({
    queryKey: KEY.templates(kind),
    queryFn: () => listEmailTemplates(kind),
    staleTime: 5 * 60_000,
  });
}

export function useUpsertTemplate() {
  const qc = useQueryClient();
  return useMutation<
    EmailTemplate | { status: string },
    Error,
    | { mode: "create"; input: CreateEmailTemplateInput }
    | { mode: "update"; id: string; input: UpdateEmailTemplateInput }
  >({
    mutationFn: (args) =>
      args.mode === "create"
        ? createEmailTemplate(args.input)
        : updateEmailTemplate(args.id, args.input),
    onSuccess: () => {
      // Invalidate every cached ``kind`` slice (cold/standard/both share rows).
      void qc.invalidateQueries({ queryKey: KEY.templatesAll });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEmailTemplate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.templatesAll });
    },
  });
}

export function useSignature() {
  const qc = useQueryClient();
  const query = useQuery<EmailSignature>({
    queryKey: KEY.signature,
    queryFn: getEmailSignature,
  });
  const mutation = useMutation({
    mutationFn: (signatureHtml: string) => setEmailSignature(signatureHtml),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.signature });
      // The effective signature is derived from this override — refresh it too.
      void qc.invalidateQueries({ queryKey: KEY.effectiveSignature });
    },
  });
  return { query, mutation };
}

export function useEffectiveSignature(account?: string, enabled = true) {
  return useQuery<EffectiveSignature>({
    // Account-scoped cache slice so a per-mailbox signature (the FROM box of
    // an inbox reply) doesn't collide with the account-agnostic resolution.
    // Prefix-compatible with ``KEY.effectiveSignature`` so ``useSignature``'s
    // invalidation still sweeps every account variant.
    queryKey: [...KEY.effectiveSignature, account ?? ""],
    // Wrap so react-query's QueryFunctionContext isn't passed as the ``account``
    // arg (getEffectiveSignature gained an optional account param — passing the
    // ref directly makes the context collapse TQueryFnData inference → TS2769).
    queryFn: () => getEffectiveSignature(account),
    enabled,
    staleTime: 5 * 60_000,
  });
}

// ─── Signature POOL (multi-signature model, Jul 2026) ──────────────────────

/** The named signature pool (user + org-scoped rows). */
export function useSignatures() {
  return useQuery<Signature[]>({
    queryKey: KEY.signatures,
    queryFn: listSignatures,
    staleTime: 5 * 60_000,
  });
}

/** Every pool mutation refreshes the pool + the derived effective signature. */
function useInvalidatePool() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY.signatures });
    void qc.invalidateQueries({ queryKey: KEY.effectiveSignature });
  };
}

export function useCreateSignature() {
  const invalidate = useInvalidatePool();
  return useMutation<Signature, Error, CreateSignatureInput>({
    mutationFn: (input) => createSignature(input),
    onSuccess: invalidate,
  });
}

export function useUpdateSignature() {
  const invalidate = useInvalidatePool();
  return useMutation<Signature, Error, { id: string; input: UpdateSignatureInput }>({
    mutationFn: ({ id, input }) => updateSignature(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSignature() {
  const invalidate = useInvalidatePool();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteSignature(id),
    onSuccess: invalidate,
  });
}

export function useSetDefaultSignature() {
  const invalidate = useInvalidatePool();
  return useMutation<Signature, Error, string>({
    mutationFn: (id) => setDefaultSignature(id),
    onSuccess: invalidate,
  });
}

/** Connected mailboxes + which pool signature is each one's default. */
export function useSignatureAccounts() {
  return useQuery<AccountSignatureBinding[]>({
    queryKey: KEY.signatureAccounts,
    queryFn: listSignatureAccounts,
    staleTime: 5 * 60_000,
  });
}

export function useSetAccountSignature() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { connectionId: string; signatureId: string | null }
  >({
    mutationFn: ({ connectionId, signatureId }) =>
      setAccountSignature(connectionId, signatureId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.signatureAccounts });
      // The per-account binding changes the resolved (effective) signature.
      void qc.invalidateQueries({ queryKey: KEY.effectiveSignature });
    },
  });
}

export function useBulkSend() {
  const qc = useQueryClient();
  return useMutation<
    BulkSendResponse,
    Error,
    { campaignId: string; input: BulkSendInput }
  >({
    mutationFn: ({ campaignId, input }) => bulkSend(campaignId, input),
    onSuccess: (_res, { campaignId }) => {
      // Rows flip to ``contacted`` in the background — nudge the prospects query.
      void qc.invalidateQueries({ queryKey: ["sales", "prospects", campaignId] });
    },
  });
}
